/* Job scheduling arithmetic: what is late, what is planned when, and what the
 * shop's history with a job looks like.
 *
 * Two dates matter and they are not the same. **Promised** is what the customer
 * was told. **Deadline** is the date it must be out by. A job whose planned work
 * window runs past its deadline is at risk even though nothing is overdue yet. */
import type { IsoDay, Job, JobDto, Plan, Stage, StageEvent, Task } from '@/types';
import { PEOPLE } from '@/data/people';
import { coatsFor } from '@/data/reference';
import { addDays, dayAsDate, isDay, today, workdaysFrom } from './time';

/** A promised day that has gone past, straight off the wire.
 *
 *  The same rule `isOverdue` applies to a job, asked the same way and against
 *  the same day — today where the shop is, never where the tablet is. The
 *  customers list uses this one because the date it draws is the server's
 *  answer to "who is waiting on us next", not a job it holds.
 *
 *  Days are ISO strings, so `<` is the correct comparison and not a shortcut:
 *  "2026-09-03" sorts before "2026-09-11" because the format was chosen for
 *  exactly that. */
export function overdueDay(day: IsoDay | null): boolean {
  return day !== null && day < today();
}

/** A job from the server, with its dates resolved into what the board reads.
 *
 *  This is the whole conversion between the two: the server keeps real days,
 *  and every screen here works in offsets from the pinned Monday in
 *  `domain/dates.ts` because the board and the schedule grid are integer
 *  arithmetic. One function, so the pin has one place to go when it goes. */
export function hydrate(dto: JobDto): Job {
  return {
    id: dto.id,
    code: dto.code,
    customerId: dto.customerId,
    customer: dto.customer,
    title: dto.title,
    // An em dash is what a screen draws for a blank, never what the record
    // holds — the column is null, and the difference matters to the server.
    ref: dto.ref ?? '—',
    colour: dto.colour,
    sheen: dto.sheen,
    profile: dto.profile,
    items: dto.items,
    stage: dto.stageId,
    boardOrder: dto.boardOrder,
    who: [...dto.who],
    site: dto.site ?? 'Site not set',
    damaged: dto.damaged,
    note: dto.note,
    // The days come across untouched. They are the same strings the `date`
    // columns hold, and the screens compare and render them as they are —
    // there is no second calendar on this side any more.
    received: dto.receivedAt,
    promised: dto.promisedAt,
    plan: {
      estStart: dto.estStartAt,
      estDays: dto.estDays,
      deadline: dto.deadlineAt,
    },
  };
}

/** A job has a promised date we can place on the schedule. */
export function scheduled(j: Job): j is Job & { promised: IsoDay } {
  return j.promised !== null;
}

/** Past its promise and not yet out of the shop.
 *
 *  Terminal-ness is the admin's to decide, so the live stage list is an
 *  argument rather than an import — a stage marked terminal in Settings takes
 *  its jobs out of the overdue count on the next render, everywhere at once. */
export function isOverdue(j: Job, stages: Stage[]): boolean {
  return overdueDay(j.promised) && !stages.find((s) => s.id === j.stage)?.terminal;
}

/** The planned work window, first day to last, or null when nothing is
 *  planned.
 *
 *  Est. days are working days, so the window runs over the weekends it spans:
 *  five days from a Thursday is out on the following Wednesday, and the racks
 *  are occupied the whole way. */
export function planWindow(p: Plan | undefined): { s: IsoDay; e: IsoDay } | null {
  if (!p || !isDay(p.estStart)) return null;
  return { s: p.estStart, e: workdaysFrom(p.estStart, p.estDays || 1) };
}

/** The day the plan finishes on — the last day of the work window, or null
 *  when nothing is planned. What a deadline has to be on or after. */
export function plannedFinish(p: Plan | undefined): IsoDay | null {
  return planWindow(p)?.e ?? null;
}

/** The hard deadline, or null. */
export function deadlineAt(p: Plan | undefined): IsoDay | null {
  return p && isDay(p.deadline) ? p.deadline : null;
}

/** Planned finish runs past the deadline. Distinct from overdue: nothing has
 *  slipped yet, but on the current estimate it will. */
export function atRisk(p: Plan | undefined): boolean {
  const win = planWindow(p);
  const dl = deadlineAt(p);
  return win !== null && dl !== null && win.e > dl;
}

/* Every board move is recorded with who and when — nearly free to store, and
   where cycle-time reporting comes from in Phase 2. Still reconstructed here
   from the stage the job has reached; when stage events land in the database
   these become real rows and this goes.

   The hours are minutes past UTC midnight rather than a wall clock, because a
   stage event is an *instant* — the screen renders it in the shop's timezone
   through `formatInstant`, the way it will when the rows are real. Building a
   local-looking string here is what the whole of `time.ts` exists to stop. */
const HISTORY_MINUTES = [460, 660, 860, 495, 815, 590, 910, 625, 475];

export function historyFor(job: Job, stages: Stage[]): StageEvent[] {
  // A job with no goods-in date has nothing to count its history from, so it
  // has none — which is honest: nothing has happened to it yet.
  if (!isDay(job.received)) return [];

  const idx = stages.findIndex((s) => s.id === job.stage);
  const walked = stages.slice(0, idx + 1);

  return walked.map((s, i, arr) => {
    const minutes = HISTORY_MINUTES[i % HISTORY_MINUTES.length]!;
    const at = new Date(dayAsDate(addDays(job.received!, i)).getTime() + minutes * 60_000);
    return {
      when: at.toISOString(),
      what: i === 0 ? `Created at ${s.name}` : `${arr[i - 1]!.name} → ${s.name}`,
      who: job.who.length ? job.who[i % job.who.length]! : 'Office',
    };
  });
}

/** The checklist a job carries. Tasks live inside the job card and never move
 *  on the board — two boards means nobody knows which one is true.
 *
 *  Keyed on the seeded stage ids, because until jobs reach the database the
 *  checklist is a fixture. A stage the admin has since added or archived simply
 *  has no task against it; nothing here assumes the list is still the seed. */
const TASK_PLAN = [
  { stage: 'goods', label: 'Count goods in against the door schedule' },
  { stage: 'goods', label: 'Check for damage and photograph anything marked' },
  { stage: 'prep', label: 'Fill and sand edges' },
  { stage: 'primer', label: 'Prime both faces' },
  { stage: 'sand', label: 'Sand back before topcoat' },
  { stage: 'topcoat', label: 'Spray topcoat' },
  { stage: 'curing', label: 'Rack to cure — 24 hours minimum' },
  { stage: 'qa', label: 'Check finish and count out against the schedule' },
  { stage: 'ready', label: 'Wrap and load for delivery' },
];

export function tasksFor(job: Job, stages: Stage[]): Task[] {
  const si = stages.findIndex((s) => s.id === job.stage);
  const who = job.who.length ? job.who : ['—'];

  return TASK_PLAN.map((t, i) => {
    const ti = stages.findIndex((s) => s.id === t.stage);
    const done = ti < si;
    return {
      label: t.stage === 'topcoat' ? `Spray topcoat — ${coatsFor(job.sheen)} coats` : t.label,
      who: who[i % who.length],
      done,
      due: done ? 'Done' : ti === si ? 'Today' : 'Upcoming',
    };
  });
}

/** Full name and shop role behind a set of initials. */
export function personFor(initials: string) {
  return PEOPLE[initials] ?? { name: initials, role: 'Unknown' };
}
